import { useMemo } from "react";
import { create } from "zustand";
import { devtools, persist } from "zustand/middleware";

import * as yup from "yup";
import "yup-phone-lite";

import { PhiFileReferenceClaimType } from "../../__generated__/mcs_backend_v1/graphql";

import translationConfigEn from "../../i18n/locales/en.json";

// MW-6024 Note: Using this prefix to identify BCBS providers, agnostic to whether the insurance provider is commercial or Medicaid
// Product doesn't currently have a solid understanding of how the flows will differ between the two
// so this is currently just the simple solution and will be revisited in the future if needed
export const BCBS_PROVIDER_LABEL_PREFIX = "BCBS";

/**
 * NOTE: these constants are actually used to define intake form schema as well!!
 *
 * The reason for this is A) de-duplicate code and B) provide type safety for accessing translation keys
 * on <SelectInput> and <RadioSelectInput> components
 */

// turns { YES: 'Yes': NO: 'No' } into typed array, e.g. `Array<'YES' | 'NO'>`

const choicesToTypedValues = <T extends Record<string, string>>(choices: T): Array<keyof T> => {
  return Object.keys(choices);
};

const PROVINCE_CODE_CHOICES = translationConfigEn.intakeAddress.provinceCodeChoices;
export const SUPPORTED_PROVINCE_CODES = choicesToTypedValues(PROVINCE_CODE_CHOICES);
//
const RELATION_TO_CHILD_CHOICES = translationConfigEn.intakeCaregiverInfo.relationshipToChildChoices;
export const SUPPORTED_RELATIONSHIP_TO_CHILD_VALUES = choicesToTypedValues(RELATION_TO_CHILD_CHOICES);
//
const GENDER_CHOICES = translationConfigEn.intakeMemberInfo.genderChoices;
export const SUPPORTED_GENDER_VALUES = choicesToTypedValues(GENDER_CHOICES);
//
const RACE_CHOICES = translationConfigEn.intakeMemberInfo.raceChoices;
export const SUPPORTED_RACE_VALUES = choicesToTypedValues(RACE_CHOICES);
//
const ETHNICITY_CHOICES = translationConfigEn.intakeMemberInfo.ethnicityChoices;
export const SUPPORTED_ETHNICITY_VALUES = choicesToTypedValues(ETHNICITY_CHOICES);
//
const HAVE_INTERNET_CHOICES = translationConfigEn.intakeMemberInfo.haveInternetChoices;
export const SUPPORTED_HAVE_INTERNET_VALUES = choicesToTypedValues(HAVE_INTERNET_CHOICES);

// consent - just a boolean. Isn't a true "form", but needs to be included in store/etc.
// Possibility that in future, multiple fields are on this page so it's a schema.
export const consentSchema = yup
  .object({
    disclosureFileName: yup.string().required(),
    disclosureMd5: yup.string().required(),
    methodOfSignature: yup.string().required(),
    signatureRecordedAt: yup.string().required(),
  })
  .required();

export type TConsentSchema = yup.InferType<typeof consentSchema>;

// NOTE: lint rule disabled so we can take advantage of yup's pretty dynamic typing
// eslint-disable-next-line @typescript-eslint/explicit-function-return-type
const requiredStringInput = (min: number | undefined = 1) => {
  return yup.string().strict().required().trim().min(min);
};

// Caregiver Info step
export const caregiverInfoSchema = yup
  .object({
    firstName: requiredStringInput(),
    lastName: requiredStringInput(),
    email: requiredStringInput().email(),
    emailMarketingOptIn: yup.boolean(),
    phoneNumber: yup.string().phone("US", "Please enter a valid phone number").required(),
    smsMarketingOptIn: yup.boolean(),
    relationshipToChild: yup.string().required().oneOf(SUPPORTED_RELATIONSHIP_TO_CHILD_VALUES),
  })
  .required();

export type TCaregiverInfoSchema = yup.InferType<typeof caregiverInfoSchema>;

// Caregiver Address step
export const caregiverAddressSchema = yup
  .object({
    address1: requiredStringInput(),
    address2: yup.string(),
    city: requiredStringInput(),
    provinceCode: yup.string().required().oneOf(SUPPORTED_PROVINCE_CODES),
    zipCode: requiredStringInput(3),
  })
  .required();

export type TCaregiverAddressSchema = yup.InferType<typeof caregiverAddressSchema>;

// Member info step
export const memberInfoSchema = yup
  .object({
    firstName: requiredStringInput(),
    lastName: requiredStringInput(),
    dateOfBirth: requiredStringInput(),
    gender: yup.string().required().oneOf(SUPPORTED_GENDER_VALUES),
    race: yup.string().required().oneOf(SUPPORTED_RACE_VALUES),
    ethnicity: yup.string().required().oneOf(SUPPORTED_ETHNICITY_VALUES),
    haveInternet: yup.string().required().oneOf(SUPPORTED_HAVE_INTERNET_VALUES),
  })
  .required();

export type TMemberInfoSchema = yup.InferType<typeof memberInfoSchema>;

// insurance info step
export const insuranceInfoSchema = yup
  .object({
    insuranceProviderId: yup.string().required().min(1),
    insuranceProviderSlug: yup.string(),
    memberId: requiredStringInput(),
    groupNumber: yup.string(),
    isChildPolicyHolder: yup.boolean().when("insuranceProviderSlug", {
      is: (label: string) => label?.includes(BCBS_PROVIDER_LABEL_PREFIX),
      then: (schema) => schema.required("This field is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
    subscriberFirstName: yup.string().when(["insuranceProviderSlug", "isChildPolicyHolder"], {
      is: (label: string, isChild: boolean) => label?.includes(BCBS_PROVIDER_LABEL_PREFIX) && !isChild,
      then: (schema) => schema.required("First name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    subscriberLastName: yup.string().when(["insuranceProviderSlug", "isChildPolicyHolder"], {
      is: (label: string, isChild: boolean) => label?.includes(BCBS_PROVIDER_LABEL_PREFIX) && !isChild,
      then: (schema) => schema.required("Last name is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    subscriberDOB: yup.string().when(["insuranceProviderSlug", "isChildPolicyHolder"], {
      is: (label: string, isChild: boolean) => label?.includes(BCBS_PROVIDER_LABEL_PREFIX) && !isChild,
      then: (schema) => schema.required("Date of birth is required"),
      otherwise: (schema) => schema.notRequired(),
    }),

    subscriberNumber: yup.string().when(["insuranceProviderSlug", "isChildPolicyHolder"], {
      is: (label: string, isChild: boolean) => label?.includes(BCBS_PROVIDER_LABEL_PREFIX) && !isChild,
      then: (schema) => schema.required("Subscriber number is required"),
      otherwise: (schema) => schema.notRequired(),
    }),
  })
  .required();

export type TInsuranceInfoSchema = yup.InferType<typeof insuranceInfoSchema>;

/**
 * FORM STATE - ENTIRE STORE
 */

export interface IntakeFormState {
  consent: undefined | TConsentSchema;
  caregiverInfo: undefined | TCaregiverInfoSchema;
  caregiverAddress: undefined | TCaregiverAddressSchema;
  memberInfo: undefined | TMemberInfoSchema;
  insuranceInfo: undefined | TInsuranceInfoSchema;
}

const initialState: IntakeFormState = {
  consent: undefined,
  caregiverInfo: undefined,
  caregiverAddress: undefined,
  memberInfo: undefined,
  insuranceInfo: undefined,
};

interface IntakeFormStore extends IntakeFormState {
  // additional data (that is not in the yup schema)
  policyHolderImageData: Partial<{
    insuranceCardFront: PhiFileReferenceClaimType;
    insuranceCardBack: PhiFileReferenceClaimType;
  }>;
  // submit steps
  handleConsentSubmit: (newData: TConsentSchema | undefined) => void;
  handleCaregiverInfoSubmit: (newData: TCaregiverInfoSchema) => void;
  handleCaregiverAddressSubmit: (newData: TCaregiverAddressSchema) => void;
  handleMemberInfoSubmit: (newData: TMemberInfoSchema) => void;
  handleInsuranceInfoSubmit: (
    newData: TInsuranceInfoSchema,
    insuranceCardFront: PhiFileReferenceClaimType | undefined,
    insuranceCardBack: PhiFileReferenceClaimType | undefined,
  ) => void;
  //
  reset: () => void;
}

export const useIntakeFormStore = create<IntakeFormStore>()(
  devtools(
    // persist to local storage automatically using key 'intake-form-state-storage'
    persist(
      (set) => ({
        consent: undefined,
        handleConsentSubmit: (newData: TConsentSchema | undefined): void => {
          if (newData) {
            return set((state): IntakeFormStore => ({ ...state, consent: { ...state.consent, ...newData } }));
          }

          return set((state): IntakeFormStore => ({ ...state, consent: undefined }));
        },
        caregiverInfo: undefined,
        handleCaregiverInfoSubmit: (newData: TCaregiverInfoSchema): void => {
          return set((state): IntakeFormStore => ({ ...state, caregiverInfo: { ...state.caregiverInfo, ...newData } }));
        },
        caregiverAddress: undefined,
        handleCaregiverAddressSubmit: (newData: TCaregiverAddressSchema): void => {
          return set(
            (state): IntakeFormStore => ({ ...state, caregiverAddress: { ...state.caregiverAddress, ...newData } }),
          );
        },
        memberInfo: undefined,
        handleMemberInfoSubmit: (newData: TMemberInfoSchema): void => {
          return set((state): IntakeFormStore => ({ ...state, memberInfo: { ...state.memberInfo, ...newData } }));
        },
        insuranceInfo: undefined,
        handleInsuranceInfoSubmit: (
          newData: TInsuranceInfoSchema,
          insuranceCardFront: PhiFileReferenceClaimType | undefined,
          insuranceCardBack: PhiFileReferenceClaimType | undefined,
        ): void => {
          return set(
            (state): IntakeFormStore => ({
              ...state,
              insuranceInfo: {
                ...state.insuranceInfo,
                ...newData,
              },
              policyHolderImageData: {
                insuranceCardBack,
                insuranceCardFront,
              },
            }),
          );
        },
        // additional data
        policyHolderImageData: {
          insuranceCardFront: undefined,
          insuranceCardBack: undefined,
        },
        //
        reset: (): void => {
          return set((state): IntakeFormStore => ({ ...state, ...initialState }));
        },
      }),
      {
        name: "intake-form-state-storage",
      },
    ),
  ),
);

/**
 * hook watches intakeFormStore to make sure everything is valid.
 * @returns Array of keys where data is invalid!!
 */
export const useIntakeFormStateValidation = (): Array<keyof IntakeFormState> => {
  const { consent } = useIntakeFormStore();
  const didGiveConsent = !!consent; // If not undefined, customer has consented

  return useMemo(() => {
    const ret: Array<keyof IntakeFormState> = [];
    if (!didGiveConsent) {
      ret.push("consent");
    }
    return ret;
  }, [didGiveConsent]);
};
